import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NativeSyntheticEvent, StyleProp, StyleSheet, Text, TextInputKeyPressEventData, TouchableOpacity, ViewStyle, View } from 'react-native';
import { DateData } from 'react-native-calendars';
import { Theme } from 'react-native-calendars/src/types';
import { format as formatDate, parseISO } from 'date-fns';
import { isMobile } from '@/utils/general/device-info';
import { MB_Modal } from '@/components/general/modal';
import { MB_ModalDropdown } from '@/components/general/dropdown';
import { mbApplyTransparency, mbPlatformStyle } from '@/utils/general/style-utils';
import { MB_TextInput, MB_TextInputInputType, MB_TextInputRef } from '@/components/general/text-input';
import { MB_SVG_Calendar } from '@/components/general/svg-components';
import { MB_Calendar } from '@/components/general/calendar';

export enum MB_DatePickerDateFormat {
  monthDayYear = 'monthDayYear',
  dayMonthYear = 'dayMonthYear',
}

const DateFormat = {
  [MB_DatePickerDateFormat.dayMonthYear]: ['D', '/', 'M', '/', 'Y'],
  [MB_DatePickerDateFormat.monthDayYear]: ['M', '/', 'D', '/', 'Y'],
};

export interface MB_DatePickerProps {
  /**
   * The initial date to show in the date picker.
   */
  initialDate?: Date;

  /**
   * The initial month to show in the date picker.
   */
  initialMonth?: Date | string;

  /**
   * The callback that is called when the date is changed.
   */
  onDateChanged?: (date?: Date) => void;

  /**
   * @description The date format to use for the date picker.
   *
   * @default MB_DatePickerDateFormat.monthDayYear
   */
  dateFormat?: MB_DatePickerDateFormat;

  /**
   * The minimum allowed date.
   */
  minDate?: Date;

  /**
   * The input's style.
   */
  style?: StyleProp<ViewStyle>;

  /**
   * The calendar's style.
   */
  calendarStyle?: StyleProp<ViewStyle>;

  /**
   * The calendar's theme.
   */
  calendarTheme?: Theme;

  /**
   * The calendar icon's container style.
   */
  calendarIconContainer?: StyleProp<ViewStyle>;
}

const formatNumber = (num?: number) => (num ? (num < 10 ? '0' + num : '' + num) : '');

const MB_DatePicker = ({
  initialDate,
  initialMonth,
  onDateChanged,
  dateFormat = MB_DatePickerDateFormat.monthDayYear,
  minDate,
  style,
  calendarStyle,
  calendarTheme,
  calendarIconContainer,
}: MB_DatePickerProps) => {
  const [didSetCurrentDateFromInitial, setDidSetCurrentDateFromInitial] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [currentDate, setCurrentDate] = useState<{ d?: string; m?: string; y?: string }>({});
  const [isDateInvalid, setIsDateInvalid] = useState(false);
  const calendarRef = useRef<TouchableOpacity>(null);
  const dayRef = useRef<MB_TextInputRef>(null);
  const monthRef = useRef<MB_TextInputRef>(null);
  const yearRef = useRef<MB_TextInputRef>(null);

  useEffect(() => {
    if (!didSetCurrentDateFromInitial && initialDate) {
      setDidSetCurrentDateFromInitial(true);
      setCurrentDate({
        d: formatNumber(initialDate.getDate()),
        m: formatNumber(initialDate.getMonth() + 1),
        y: formatNumber(initialDate.getFullYear()),
      });
    }
  }, [didSetCurrentDateFromInitial, initialDate]);

  const onDatePicked = (selectedDate: DateData) => {
    setCurrentDate({
      d: formatNumber(selectedDate.day),
      m: formatNumber(selectedDate.month),
      y: formatNumber(selectedDate.year),
    });
    setIsModalVisible(false);

    onDateChanged?.(parseISO(selectedDate.dateString));
  };

  const onDayChanged = useCallback(
    (day: string) => {
      if (day.length <= 2) {
        if (day && currentDate?.m && currentDate?.y) {
          const date = Date.parse(`${currentDate.m}/${day}/${currentDate.y}`);
          if (!isNaN(date)) {
            setIsDateInvalid(false);

            // Date object expects the month to start from 0
            onDateChanged?.(new Date(Number(currentDate.y), Number(currentDate.m) - 1, Number(day)));
          } else {
            onDateChanged?.(undefined);
            setIsDateInvalid(true);
          }
        } else {
          onDateChanged?.(undefined);
        }
        setCurrentDate({ ...currentDate, d: day });
      }
      if (day.length === 2) {
        if (dateFormat === MB_DatePickerDateFormat.dayMonthYear) {
          monthRef.current?.focus();
        } else if (dateFormat === MB_DatePickerDateFormat.monthDayYear) {
          yearRef.current?.focus();
        }
      }
    },
    [currentDate, dateFormat, onDateChanged]
  );

  const onDayKeyPressed = (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    if (e.nativeEvent.key === 'Tab') {
      e.preventDefault();
      if (dateFormat === MB_DatePickerDateFormat.dayMonthYear) {
        monthRef.current?.focus();
      } else if (dateFormat === MB_DatePickerDateFormat.monthDayYear) {
        yearRef.current?.focus();
      }
    }
  };

  const onMonthChanged = useCallback(
    (month: string) => {
      if (month.length <= 2) {
        if (month && currentDate?.d && currentDate?.y) {
          const date = Date.parse(`${month}/${currentDate.d}/${currentDate.y}`);
          if (!isNaN(date)) {
            // Date object expects the month to start from 0
            onDateChanged?.(new Date(Number(currentDate.y), Number(month) - 1, Number(currentDate.d)));
            setIsDateInvalid(false);
          } else {
            onDateChanged?.(undefined);
            setIsDateInvalid(true);
          }
        } else {
          onDateChanged?.(undefined);
        }
        setCurrentDate({ ...currentDate, m: month });
      }
      if (month.length === 2) {
        if (dateFormat === MB_DatePickerDateFormat.dayMonthYear) {
          yearRef.current?.focus();
        } else if (dateFormat === MB_DatePickerDateFormat.monthDayYear) {
          dayRef.current?.focus();
        }
      }
    },
    [currentDate, dateFormat, onDateChanged]
  );

  const onMonthKeyPressed = (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    if (e.nativeEvent.key === 'Tab') {
      e.preventDefault();
      if (dateFormat === MB_DatePickerDateFormat.dayMonthYear) {
        yearRef.current?.focus();
      } else if (dateFormat === MB_DatePickerDateFormat.monthDayYear) {
        dayRef.current?.focus();
      }
    }
  };

  const onYearChanged = useCallback(
    (year: string) => {
      if (year.length <= 4) {
        if (year && currentDate?.d && currentDate?.m) {
          const date = Date.parse(`${currentDate.m}/${currentDate.d}/${year}`);
          if (year.length !== 4 || isNaN(date)) {
            onDateChanged?.(undefined);
            setIsDateInvalid(true);
          } else {
            // Date object expects the month to start from 0
            onDateChanged?.(new Date(Number(year), Number(currentDate.m) - 1, Number(currentDate.d)));
            setIsDateInvalid(false);
          }
        } else {
          onDateChanged?.(undefined);
        }
        setCurrentDate({ ...currentDate, y: year });
      }
      if (year.length === 4) {
        yearRef.current?.blur();
      }
    },
    [currentDate, onDateChanged]
  );

  const onFocus = () => {
    if (dateFormat === MB_DatePickerDateFormat.dayMonthYear) {
      if (!currentDate.d) {
        dayRef.current?.focus();
      } else if (!currentDate.m) {
        monthRef.current?.focus();
      } else if (!currentDate.y) {
        yearRef.current?.focus();
      } else {
        dayRef.current?.focus();
      }
    } else if (dateFormat === MB_DatePickerDateFormat.monthDayYear) {
      if (!currentDate.m) {
        monthRef.current?.focus();
      } else if (!currentDate.d) {
        dayRef.current?.focus();
      } else if (!currentDate.y) {
        yearRef.current?.focus();
      } else {
        monthRef.current?.focus();
      }
    }
  };

  useEffect(() => {
    calendarRef.current?.measure((_, __, w, h, x, y) => {
      setPosition({ x, y, w, h });
    });
  }, [isModalVisible]);

  const { x, y, w, h } = position;

  const initialMonthFormatted = (): string | undefined => {
    if (!initialMonth) {
      return undefined;
    } else if (typeof initialMonth === 'string') {
      return initialMonth;
    }

    return formatDate(initialMonth, 'yyyy-MM-dd');
  };

  const Modal = useMemo(
    () =>
      ({ children }: { children: React.JSX.Element }) => {
        if (isMobile) {
          return (
            <MB_Modal
              isVisible={isModalVisible}
              onDismiss={() => setIsModalVisible(false)}
              animationIn={'fadeIn'}
              animationOut={'fadeOut'}
              title="Please Select Date"
              childrenWrapperStyle={styles.mobileCalendarWrapper}
            >
              {children}
            </MB_Modal>
          );
        }
        return (
          <MB_ModalDropdown visible={isModalVisible} callerX={x} callerY={y} callerWidth={w} callerHeight={h} onClose={() => setIsModalVisible(false)}>
            {children}
          </MB_ModalDropdown>
        );
      },
    [h, isModalVisible, w, x, y]
  );

  return (
    <TouchableOpacity activeOpacity={1} onFocus={onFocus} style={[styles.container, style, { borderColor: mbApplyTransparency(isDateInvalid ? '#FF3B3B' : '#BAC0CA', 0.4) }]}>
      <Modal>
        <MB_Calendar
          onDayChanged={onDatePicked}
          calendarStyle={[styles.calendarStyle, calendarStyle]}
          minDate={minDate}
          theme={calendarTheme}
          initialSelectedDate={
            currentDate.y === undefined || currentDate.m === undefined || currentDate.d === undefined ? undefined : `${currentDate.y}-${currentDate.m}-${currentDate.d}`
          }
          initialSelectedMonth={initialMonthFormatted()}
          selectedDateContainerStyle={{ backgroundColor: '#9A4C95' }}
        />
      </Modal>

      {DateFormat[dateFormat].map((currentField, index) => {
        if (currentField === 'D') {
          return (
            <TouchableOpacity key={index} onFocus={e => e.stopPropagation()}>
              <MB_TextInput
                MB_Ref={dayRef}
                placeholder="DD"
                value={currentDate?.d ?? ''}
                textInputStyle={styles.calendarTextInputStyle}
                style={styles.calendarTextInputContainer}
                onChangeText={onDayChanged}
                onKeyPressed={onDayKeyPressed}
                inputType={MB_TextInputInputType.numbers}
              />
            </TouchableOpacity>
          );
        } else if (currentField === 'M') {
          return (
            <TouchableOpacity key={index} onFocus={e => e.stopPropagation()}>
              <MB_TextInput
                MB_Ref={monthRef}
                placeholder="MM"
                value={currentDate?.m ?? ''}
                textInputStyle={styles.calendarTextInputStyle}
                style={styles.calendarTextInputContainer}
                onChangeText={onMonthChanged}
                onKeyPressed={onMonthKeyPressed}
                inputType={MB_TextInputInputType.numbers}
              />
            </TouchableOpacity>
          );
        } else if (currentField === 'Y') {
          return (
            <TouchableOpacity key={index} onFocus={e => e.stopPropagation()}>
              <MB_TextInput
                MB_Ref={yearRef}
                placeholder="YYYY"
                value={currentDate?.y ?? ''}
                textInputStyle={styles.calendarTextInputStyle}
                style={[styles.calendarTextInputContainer, { width: isMobile ? 38 : 50 }]}
                onChangeText={onYearChanged}
                inputType={MB_TextInputInputType.numbers}
              />
            </TouchableOpacity>
          );
        } else {
          return (
            <Text
              key={index}
              style={[
                styles.calendarTextInputStyle,
                {
                  color: currentDate.d && currentDate.m && currentDate.y ? '#FFFFFF' : mbApplyTransparency('#BAC0CA', 0.4),
                },
              ]}
            >
              {currentField}
            </Text>
          );
        }
      })}
      <View style={[{ flex: 1, alignItems: 'flex-end' }, calendarIconContainer]}>
        <TouchableOpacity onFocus={e => e.stopPropagation()} ref={calendarRef} onPress={() => setIsModalVisible(true)} style={styles.iconContainer}>
          <MB_SVG_Calendar size={18} color="#BAC0CA" />
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );
};

export { MB_DatePicker };

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    height: isMobile ? 44 : 54,
    borderWidth: 1,
    borderRadius: 6,
    paddingLeft: isMobile ? 10 : 22,
    paddingRight: isMobile ? 6 : 10,
    backgroundColor: '#1D1A31',
    ...mbPlatformStyle({
      mobile: {
        maxWidth: 185,
        alignSelf: 'baseline',
      },
      web: {
        width: 386,
      },
    }),
  },
  iconContainer: {
    padding: isMobile ? 4 : 8,
    backgroundColor: mbApplyTransparency('#FFFFFF', 0.07),
    borderRadius: 6,
    marginLeft: 12,
  },
  calendarStyle: {
    backgroundColor: '#1D1A31',
    ...mbPlatformStyle({
      mobile: {
        maxWidth: 350,
        width: '100%',
      },
    }),
  },
  mobileCalendarWrapper: {
    alignSelf: 'stretch',
    alignItems: 'stretch',
  },
  calendarTextInputStyle: {
    lineHeight: undefined,
    fontWeight: isMobile ? 'normal' : '500',
    fontSize: isMobile ? 12 : 14,
    color: '#FFFFFF',
    textAlign: 'center',
  },
  calendarTextInputContainer: {
    borderWidth: 0,
    width: 25,
    paddingHorizontal: 0,
    paddingEnd: 0,
  },
});
